import React, { useEffect, useRef, useState } from "react";
import "./AdSenseBanner.css";

export const ADSENSE_CLIENT = "ca-pub-1539854949018984";
/** AdSense display unit: “web horizontal banner”. */
export const ADSENSE_SLOT_WEB_HORIZONTAL = "6486512092";
/** Site-wide kill switch — empty units while AdSense is still Getting ready. */
export const ADSENSE_ENABLED = false;

const ADSENSE_SCRIPT_ID = "pi-adsense-loader";
const ADSENSE_SCRIPT_SRC =
    `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;

const PI_CONSENT_COOKIE = "pi_consent";
const LOG_PREFIX = "[PI AdSense]";

let adSenseScriptPromise = null;

function adsenseLog(...args) {
    try {
        // eslint-disable-next-line no-console
        console.log(LOG_PREFIX, ...args);
    } catch {
        /* ignore */
    }
}

function adsenseWarn(...args) {
    try {
        // eslint-disable-next-line no-console
        console.warn(LOG_PREFIX, ...args);
    } catch {
        /* ignore */
    }
}

function enableAdSenseTestMode() {
    if (typeof window === "undefined") return;
    window.google_adtest = "on";
}

function ensureAdSenseScript({ testMode = false } = {}) {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return Promise.resolve(false);
    }
    if (testMode) {
        enableAdSenseTestMode();
    }
    if (adSenseScriptPromise) {
        adsenseLog("script: reuse in-flight/cached load promise");
        return adSenseScriptPromise;
    }
    const existing = document.getElementById(ADSENSE_SCRIPT_ID);
    if (existing) {
        adsenseLog("script: found existing tag", {
            loaded: existing.getAttribute("data-loaded") === "1",
        });
        adSenseScriptPromise = new Promise((resolve) => {
            if (existing.getAttribute("data-loaded") === "1") {
                resolve(true);
                return;
            }
            existing.addEventListener("load", () => resolve(true), { once: true });
            existing.addEventListener("error", () => resolve(false), { once: true });
        });
        return adSenseScriptPromise;
    }
    adsenseLog("script: injecting", ADSENSE_SCRIPT_SRC);
    adSenseScriptPromise = new Promise((resolve) => {
        const script = document.createElement("script");
        script.id = ADSENSE_SCRIPT_ID;
        script.async = true;
        script.src = ADSENSE_SCRIPT_SRC;
        script.crossOrigin = "anonymous";
        script.addEventListener(
            "load",
            () => {
                script.setAttribute("data-loaded", "1");
                adsenseLog("script: load ok");
                resolve(true);
            },
            { once: true },
        );
        script.addEventListener(
            "error",
            () => {
                adsenseWarn("script: load FAILED");
                resolve(false);
            },
            { once: true },
        );
        document.head.appendChild(script);
    });
    return adSenseScriptPromise;
}

/** Cookie fallback when PIConsent has not finished restoring yet. */
function readMarketingFromCookie() {
    if (typeof document === "undefined") return null;
    try {
        const match = document.cookie.match(
            new RegExp(`(?:^|; )${PI_CONSENT_COOKIE}=([^;]*)`),
        );
        if (!match) return null;
        const parsed = JSON.parse(decodeURIComponent(match[1]));
        if (parsed && typeof parsed.m === "boolean") {
            return parsed.m;
        }
    } catch {
        /* ignore */
    }
    return null;
}

function readMarketingConsent() {
    if (typeof window === "undefined") return false;
    const PI = window.PIConsent;
    if (PI && typeof PI.getState === "function") {
        const state = PI.getState();
        if (state && state.decided) {
            return !!state.marketing;
        }
    }
    if (PI && typeof PI.has === "function" && PI.has("marketing")) {
        return true;
    }
    const fromCookie = readMarketingFromCookie();
    return fromCookie === true;
}

function describeConsent() {
    const PI = typeof window !== "undefined" ? window.PIConsent : null;
    return {
        allowed: readMarketingConsent(),
        piState: PI?.getState?.() ?? null,
        cookieMarketing: readMarketingFromCookie(),
    };
}

function useMarketingConsent() {
    const [allowed, setAllowed] = useState(readMarketingConsent);

    useEffect(() => {
        const sync = () => {
            const next = readMarketingConsent();
            adsenseLog("consent sync", { ...describeConsent(), next });
            setAllowed(next);
        };
        sync();
        if (typeof window === "undefined") return undefined;
        window.PIConsent?.onConsent?.("marketing", sync);
        window.addEventListener("pi-consent-update", sync);
        return () => window.removeEventListener("pi-consent-update", sync);
    }, []);

    return allowed;
}

function waitForAdWidth(el, framesLeft = 90) {
    return new Promise((resolve) => {
        const tick = (left) => {
            if (!el) {
                resolve(false);
                return;
            }
            if (el.offsetWidth > 0) {
                resolve(true);
                return;
            }
            if (left <= 0) {
                resolve(false);
                return;
            }
            requestAnimationFrame(() => tick(left - 1));
        };
        tick(framesLeft);
    });
}

function pushAdSense(el) {
    if (!el || el.getAttribute("data-adsbygoogle-status")) {
        adsenseLog("push skipped", {
            hasEl: !!el,
            status: el?.getAttribute("data-adsbygoogle-status") || null,
        });
        return false;
    }
    try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        adsenseLog("push({}) done", {
            statusAfter: el.getAttribute("data-adsbygoogle-status"),
            width: el.offsetWidth,
            height: el.offsetHeight,
        });
        return true;
    } catch (err) {
        adsenseWarn("push({}) threw", err);
        return false;
    }
}

function logFillSnapshot(el, label) {
    if (!el) {
        adsenseLog(label, { hasEl: false });
        return;
    }
    const iframe = el.querySelector("iframe");
    const payload = {
        status: el.getAttribute("data-adsbygoogle-status"),
        adStatus: el.getAttribute("data-ad-status"),
        width: el.offsetWidth,
        height: el.offsetHeight,
        iframe: iframe
            ? {
                width: iframe.offsetWidth,
                height: iframe.offsetHeight,
                src: (iframe.src || "").slice(0, 160),
                id: iframe.id || "",
            }
            : null,
    };
    adsenseLog(label, JSON.stringify(payload));
}

/**
 * Responsive AdSense display unit. Renders only after marketing consent (Law 25).
 * Loads adsbygoogle.js only after consent (Consent Mode v2 + CMP).
 *
 * @param {object} props
 * @param {string} [props.slot]
 * @param {string} [props.className]
 * @param {boolean} [props.force] — show and load the unit even when `ADSENSE_ENABLED` is false
 * @param {boolean} [props.debug] — outline the slot (test page)
 */
export default function AdSenseBanner({
    slot = ADSENSE_SLOT_WEB_HORIZONTAL,
    className = "",
    variant = "default",
    force = false,
    debug = false,
}) {
    const asideRef = useRef(null);
    const insRef = useRef(null);
    const pushedRef = useRef(false);
    const [creative, setCreative] = useState("pending");
    const allowed = useMarketingConsent();
    const isDevelopment = process.env.NODE_ENV === "development";
    const useLiveUnit = force || ADSENSE_ENABLED;
    const isLocalHost =
        typeof window !== "undefined"
        && /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);

    useEffect(() => {
        adsenseLog("mount/render gate", {
            enabled: ADSENSE_ENABLED,
            force,
            useLiveUnit,
            allowed,
            isDevelopment,
            slot,
            client: ADSENSE_CLIENT,
            href: typeof window !== "undefined" ? window.location.href : null,
            ...describeConsent(),
        });
    }, [allowed, isDevelopment, slot, force, useLiveUnit]);

    useEffect(() => {
        if (!useLiveUnit) {
            return undefined;
        }
        if (!force && !allowed) {
            adsenseLog("init skipped: marketing consent not granted");
            return undefined;
        }
        if (!force && isDevelopment) {
            adsenseWarn(
                "init skipped: NODE_ENV=development — showing placeholder only (real ads need production build / deployed site)",
            );
            return undefined;
        }

        if (force && typeof window.gtag === "function") {
            window.gtag("consent", "update", {
                ad_storage: "granted",
                ad_user_data: "granted",
                ad_personalization: "granted",
            });
            adsenseLog("force: consent mode ads granted for this page");
        }

        pushedRef.current = false;
        let cancelled = false;
        let observer = null;
        let fillTimer = null;

        const tryPush = async (reason) => {
            if (cancelled || pushedRef.current) return;
            const el = insRef.current;
            adsenseLog("tryPush", { reason, hasEl: !!el });
            if (!el) return;
            const ready = await ensureAdSenseScript({ testMode: force || isLocalHost });
            if (cancelled) {
                adsenseLog("tryPush aborted: effect cleaned up (Strict Mode remount)");
                return;
            }
            if (!ready) {
                adsenseWarn("tryPush aborted: script not ready", { ready });
                return;
            }
            const hasWidth = await waitForAdWidth(el);
            adsenseLog("layout width", { hasWidth, width: el.offsetWidth });
            if (cancelled || !hasWidth) {
                adsenseWarn("tryPush aborted: zero width");
                return;
            }
            if (pushAdSense(el)) {
                pushedRef.current = true;
                fillTimer = window.setTimeout(() => {
                    logFillSnapshot(el, "fill check +2s");
                }, 2000);
            }
        };

        const start = () => {
            if (cancelled) return;
            const root = asideRef.current;
            if (!root || typeof IntersectionObserver === "undefined") {
                tryPush("no-observer");
                return;
            }
            observer = new IntersectionObserver(
                (entries) => {
                    const hit = entries.some((entry) => entry.isIntersecting);
                    adsenseLog("IntersectionObserver", {
                        hit,
                        ratios: entries.map((e) => e.intersectionRatio),
                    });
                    if (hit) {
                        tryPush("intersecting");
                        if (observer) {
                            observer.disconnect();
                            observer = null;
                        }
                    }
                },
                { root: null, rootMargin: "240px 0px", threshold: 0 },
            );
            observer.observe(root);
            tryPush("immediate");
        };

        start();

        return () => {
            cancelled = true;
            if (observer) observer.disconnect();
            if (fillTimer) window.clearTimeout(fillTimer);
        };
    }, [allowed, isDevelopment, slot, force, useLiveUnit, isLocalHost]);

    useEffect(() => {
        if (!debug) {
            return undefined;
        }
        const el = insRef.current;
        if (!el) {
            return undefined;
        }
        let cancelled = false;
        const apply = () => {
            if (cancelled) return;
            const adStatus = el.getAttribute("data-ad-status");
            adsenseLog("data-ad-status", adStatus);
            if (adStatus === "filled") {
                setCreative("filled");
            } else if (adStatus === "unfilled") {
                setCreative("unfilled");
            }
        };
        apply();
        const mo = new MutationObserver(apply);
        mo.observe(el, {
            attributes: true,
            attributeFilter: ["data-ad-status", "data-adsbygoogle-status"],
        });
        const timer = window.setTimeout(() => {
            if (cancelled) return;
            setCreative((prev) => (prev === "filled" ? prev : "unfilled"));
        }, 2500);
        return () => {
            cancelled = true;
            mo.disconnect();
            window.clearTimeout(timer);
        };
    }, [debug, useLiveUnit, force, slot]);

    if (!useLiveUnit || (!force && !allowed && !isDevelopment)) {
        return null;
    }

    const showPlaceholder = isDevelopment && !force;

    return (
        <aside
            ref={asideRef}
            className={[
                "pi-ad-banner",
                variant === "page" ? "pi-ad-page-footer" : "",
                debug ? "pi-ad-banner--debug" : "",
                debug && creative !== "filled" ? "pi-ad-banner--unfilled" : "",
                className,
            ]
                .filter(Boolean)
                .join(" ")}
            aria-label="Advertisement"
        >
            {showPlaceholder ? (
                <div className="pi-ad-banner__dev">AdSense horizontal banner (dev)</div>
            ) : (
                <>
                    <ins
                        ref={insRef}
                        className="adsbygoogle"
                        style={{ display: "block", width: "100%", minHeight: 280 }}
                        data-ad-client={ADSENSE_CLIENT}
                        data-ad-slot={slot}
                        data-ad-format="auto"
                        data-full-width-responsive="true"
                        {...(isLocalHost || force ? { "data-adtest": "on" } : {})}
                    />
                    {debug && creative !== "filled" ? (
                        <div className="pi-ad-banner__overlay">
                            {creative === "pending"
                                ? "Requesting AdSense…"
                                : "AdSense unfilled — Google sent no creative"}
                        </div>
                    ) : null}
                </>
            )}
        </aside>
    );
}
